import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getSystemSettings } from '@/services/system';
import { Sentry } from '@/services/sentry';

export const useSystemStore = defineStore('system', () => {
  // State
  const languages = ref([]);
  const locales = ref([]);
  const currency = ref([]);
  const oddsFormat = ref([]);
  const timezones = ref([]);
  const countries = ref({}); // Object with country code as key, country name as value

  const isLoading = ref(false);
  const error = ref(null);
  const isLoaded = ref(false);

  // Actions
  const fetchSystemSettings = async () => {
    // Prevent multiple simultaneous fetches
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const data = await getSystemSettings();

      languages.value = data.languages || [];
      locales.value = data.locales || [];
      currency.value = data.currency || [];
      oddsFormat.value = data.oddsFormat || [];
      timezones.value = data.timezones || [];
      countries.value = data.countries || {};

      isLoaded.value = true;

      return data;
    } catch (error) {
      console.error('❌ Failed to fetch system settings:', error);
      Sentry.captureException(error);

      throw new Error(error?.response?.data?.detail || 'Failed to load system settings');
    } finally {
      isLoading.value = false;
    }
  };

  // Reset store (useful for testing or logout scenarios)
  const reset = () => {
    languages.value = [];
    locales.value = [];
    currency.value = [];
    oddsFormat.value = [];
    timezones.value = [];
    countries.value = {};
    isLoaded.value = false;
    error.value = null;
  };

  return {
    // State
    languages,
    locales,
    currency,
    oddsFormat,
    timezones,
    countries,
    isLoading,
    error,
    isLoaded,

    // Actions
    fetchSystemSettings,
    reset
  };
});
